// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, Fragment } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { CSSProperties } from 'react'
import { ShieldCheck, FileText, ExternalLink, Upload, Trash2, MessageSquare } from 'lucide-react'
import * as api from '../api/client'
import { useAuth } from '../auth/AuthContext'
import DocumentComments from '../components/secureshare/DocumentComments'
import SearchableSelect from '../components/common/SearchableSelect'
import TypedDeleteConfirm from '../components/common/TypedDeleteConfirm'
import { debugError } from '../api/errors'
import { formatDob, formatUsDateTime, matchesDob } from '../api/format'
import { ColumnFilterButton, ColumnFilterChips, applyColumnFilters, filterToken, type ColumnFilters } from '../components/common/ColumnFilter'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../components/Pager'

interface Props { onScreenEnter?: () => void }

// Document comment thread is built and merged but parked pending a business/compliance decision
// (comment bodies are free text and would carry PHI in the TDE tier only). Flip to true to enable.
const COMMENTS_ENABLED = false

interface SecureShareDoc {
  id: string
  patient_id: string
  patient_name?: string | null
  document_type?: string | null
  title?: string | null
  file_name?: string | null
  lifecycle_status?: string | null
  size_bytes?: number | null
  received_at?: string | null
  submitted_by_external?: boolean
  can_delete?: boolean
  patient_dob?: string | null
  uploaded_by?: string | null
}

// Valid Secure Share document types — MUST stay in sync with the CK_pdoc_type DB constraint
// (db/migrations/151_patient_documents.sql). Free text here fails the INSERT, so this is a dropdown.
const DOC_TYPES: ReadonlyArray<[string, string]> = [
  ['chart_notes', 'Chart / progress notes'],
  ['lab_results', 'Lab results'],
  ['nursing_notes', 'Nursing notes'],
  ['pharmacist_assessment', 'Pharmacist assessment'],
  ['consent_hipaa', 'HIPAA consent'],
  ['smn', 'Statement of medical necessity'],
  ['pa', 'Prior authorization'],
  ['appeal', 'Appeal'],
  ['benefits_verification', 'Benefits verification'],
  ['copay_pap', 'Copay / patient assistance'],
  ['rems', 'REMS'],
  ['enrollment_hub', 'Enrollment / hub'],
  ['rx', 'Prescription (Rx)'],
  ['refill', 'Refill'],
  ['delivery_confirmation', 'Delivery confirmation'],
  ['discontinuation_transfer', 'Discontinuation / transfer'],
]

/**
 * Secure Share — pharmacy inbox. Lists documents that providers/nurses have submitted IN for this
 * pharmacy's patients (source='secure_share'), newest first, with open/download via the existing
 * patient-document read-only SAS endpoint. Only reachable when the per-pharmacy `secure_share`
 * module is enabled (nav item + route are both module-gated).
 */
export default function SecureSharePage({ onScreenEnter }: Props) {
  useEffect(() => { onScreenEnter?.() }, [onScreenEnter])

  const { role } = useAuth()
  const isProviderViewer = role === 'provider' || role === 'provider_nurse'

  const [docs, setDocs] = useState<SecureShareDoc[] | null>(null)
  const [pendingDelete, setPendingDelete] = useState<SecureShareDoc | null>(null)
  // BA: search / filters / sorting over the document list (client-side; list is TOP 500)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'received_at', dir: -1 })
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [openComments, setOpenComments] = useState<string | null>(null)

  // Deep-link: ?doc=<id> (from the Secure Share email/notification) scrolls to + highlights that row.
  const [searchParams] = useSearchParams()
  const focusDocId = searchParams.get('doc')
  const [highlightId, setHighlightId] = useState<string | null>(null)

  // Upload form state
  const [showUpload, setShowUpload] = useState(false)
  const [patients, setPatients] = useState<api.PatientLite[]>([])
  const [form, setForm] = useState<{ patientId: string; documentType: string; title: string; notes: string }>(
    { patientId: '', documentType: 'chart_notes', title: '', notes: '' })
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitMsg, setSubmitMsg] = useState<string | null>(null)
  // Placeholder ("patient not listed") mode — provider uploads for someone not yet enrolled in PioneerRx.
  const [newPatientMode, setNewPatientMode] = useState(false)
  const [newPatient, setNewPatient] = useState<{ name: string; dob: string }>({ name: '', dob: '' })
  const [myPlaceholders, setMyPlaceholders] = useState<api.SecureSharePlaceholder[]>([])
  const [placeholderSel, setPlaceholderSel] = useState('') // a placeholder id, '__new__', or ''

  function load(searchTerm?: string) {
    setError(null)
    api.listSecureShareDocuments(searchTerm)
      .then(r => setDocs(r.documents ?? []))
      .catch(e => { setDocs([]); setError(debugError('Open Secure Share (list documents)', e)) })
  }
  useEffect(() => { load() }, [])
  // ASP-76 (same class as ASP-75): the list is capped at 500 newest, so client-side
  // search cannot see older documents. Typing re-queries the SERVER with the term;
  // clearing it restores the default newest-500 list. Client filters still apply on
  // top of whatever came back, so behavior is unchanged for small pharmacies.
  useEffect(() => {
    const term = q.trim()
    const t = setTimeout(() => load(term.length >= 2 ? term : undefined), 350)
    return () => clearTimeout(t)
  }, [q])

  // Once documents are loaded, bring the deep-linked document into view and flash it.
  useEffect(() => {
    if (!focusDocId || !docs || !docs.some(d => d.id === focusDocId)) return
    const el = document.getElementById(`ssdoc-${focusDocId}`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlightId(focusDocId)
    const t = setTimeout(() => setHighlightId(null), 4000)
    return () => clearTimeout(t)
  }, [docs, focusDocId])

  function openUpload() {
    setShowUpload(true)
    setSubmitMsg(null)
    // Always (re)load so a failure here is visible instead of leaving an empty patient list silently.
    api.listPatientsLite()
      .then(r => setPatients(r.items ?? []))
      .catch(e => setSubmitMsg(debugError('Hit upload (load patient list)', e)))
  }

  async function submitUpload() {
    if (!file) { setSubmitMsg('Choose a file to upload.'); return }
    if (!form.documentType.trim()) { setSubmitMsg('Enter a document type.'); return }
    if (newPatientMode) {
      if (placeholderSel === '__new__') {
        if (!newPatient.name.trim()) { setSubmitMsg('Enter the patient name.'); return }
        if (!newPatient.dob) { setSubmitMsg('Enter the patient date of birth.'); return }
      } else if (!placeholderSel) {
        setSubmitMsg('Pick a previous placeholder or create a new one.'); return
      }
    } else if (!form.patientId) {
      setSubmitMsg('Select a patient.'); return
    }
    setSubmitting(true); setSubmitMsg(null)
    try {
      let patientId = form.patientId
      if (newPatientMode) {
        if (placeholderSel === '__new__') {
          try {
            const p = await api.createSecureSharePlaceholderPatient(newPatient.name.trim(), newPatient.dob)
            patientId = p.id
          } catch (e) {
            setSubmitMsg(debugError('Add new (create placeholder patient)', e)); return
          }
        } else {
          patientId = placeholderSel
        }
      }
      try {
        await api.submitSecureShareDocument(patientId, file, {
          documentType: form.documentType.trim(),
          title: form.title.trim() || undefined,
          notes: form.notes.trim() || undefined,
        })
      } catch (e) {
        setSubmitMsg(debugError('Upload document', e)); return
      }
      setShowUpload(false)
      setFile(null)
      setForm({ patientId: '', documentType: 'chart_notes', title: '', notes: '' })
      setNewPatientMode(false)
      setNewPatient({ name: '', dob: '' })
      setPlaceholderSel('')
      load()
    } finally {
      setSubmitting(false)
    }
  }

  async function openDoc(d: SecureShareDoc) {
    setBusyId(d.id)
    setError(null)
    try {
      const r = await api.downloadSecureShareDoc(d.patient_id, d.id)
      if (r?.url) window.open(r.url, '_blank', 'noopener')
      else setError('Download · server returned no download URL (no error code)')
    } catch (e) {
      setError(debugError('Download document', e))
    } finally {
      setBusyId(null)
    }
  }

  async function changeStatus(d: SecureShareDoc, status: string) {
    if (status === d.lifecycle_status) return
    setError(null)
    // optimistic; revert from the server if the call fails
    setDocs(prev => prev ? prev.map(x => x.id === d.id ? { ...x, lifecycle_status: status } : x) : prev)
    try {
      await api.setSecureShareDocStatus(d.id, status)
    } catch (e) {
      setError(debugError('Edit document status', e))
      load()
    }
  }

  // BA-27: typed-confirm delete — arms only after the user types DELETE, so a
  // stray double-Enter can never destroy a document.
  function deleteDoc(d: SecureShareDoc) { setPendingDelete(d) }
  async function reallyDelete() {
    const d = pendingDelete
    if (!d) return
    setBusyId(d.id)
    setError(null)
    try {
      await api.deleteSecureShareDocument(d.id)
      setDocs(prev => prev ? prev.filter(x => x.id !== d.id) : prev)
      setPendingDelete(null)
    } catch (e) {
      setError(debugError('Delete document', e))
    } finally {
      setBusyId(null)
    }
  }

  const needle = q.trim().toLowerCase()
  // G-UI-1 / ASP-68 header filters — Gate-16-safe display tokens for date columns.
  const [colFilters, setColFilters] = useState<ColumnFilters>({})
  const ssValueOf = (d: any, key: string) => {
    switch (key) {
      case 'patient_dob': return d.patient_dob ? formatDob(d.patient_dob) : null
      case 'received_at': return d.received_at ? new Date(d.received_at).toLocaleDateString() : null
      case 'lifecycle_status': return d.lifecycle_status || 'received'
      case 'title': return d.title || d.file_name
      default: return d[key]
    }
  }
  const preHeader = (docs ?? [])
    .filter(d => !statusFilter || (d.lifecycle_status || 'received') === statusFilter)
    .filter(d => !typeFilter || d.document_type === typeFilter)
    .filter(d => !needle ||
      (d.patient_name ?? '').toLowerCase().includes(needle) ||
      (d.title ?? '').toLowerCase().includes(needle) ||
      (d.file_name ?? '').toLowerCase().includes(needle) ||
      (d.uploaded_by ?? '').toLowerCase().includes(needle) ||
      (d.document_type ?? '').toLowerCase().includes(needle) ||
      matchesDob(q, d.patient_dob))
  const distinctFor = (key: string): string[] => {
    const others: ColumnFilters = Object.fromEntries(Object.entries(colFilters).filter(([k]) => k !== key))
    const base = applyColumnFilters(preHeader, others, ssValueOf)
    const seen = new Set<string>()
    for (const r of base) seen.add(filterToken(ssValueOf(r, key)))
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }
  const shown = applyColumnFilters(preHeader, colFilters, ssValueOf)
    .sort((a, b) => {
      const k = sort.key
      const va = k === 'received_at' ? (a.received_at ?? '') : k === 'patient_dob' ? (a.patient_dob ?? '')
        : String((a as any)[k] ?? '').toLowerCase()
      const vb = k === 'received_at' ? (b.received_at ?? '') : k === 'patient_dob' ? (b.patient_dob ?? '')
        : String((b as any)[k] ?? '').toLowerCase()
      return va < vb ? -sort.dir : va > vb ? sort.dir : 0
    })
  // resetKey: a stable string of everything that reshapes `shown`, so any filter/sort change jumps back to page 1.
  const pagedResetKey = `${needle}|${statusFilter}|${typeFilter}|${sort.key}:${sort.dir}|`
    + Object.entries(colFilters).map(([k, v]) => `${k}=${Array.from(v).sort().join(',')}`).join(';')
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(shown, DEFAULT_PAGE_SIZE, pagedResetKey)
  const toggleSort = (key: string) =>
    setSort(s => s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 })
  const sortTh = (key: string, label: string) => (
    <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(key)}
        aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'} scope="col">
      {label}{sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}{' '}
      <ColumnFilterButton label={label}
        getValues={() => distinctFor(key)}
        selected={colFilters[key] ?? new Set()}
        onChange={next => setColFilters(f => ({ ...f, [key]: next }))} />
    </th>
  )

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <ShieldCheck size={22} aria-hidden="true" />
        <h1 style={{ fontSize: 22, margin: 0 }}>Secure Share</h1>
        <div style={{ flex: 1 }} />
        <button onClick={openUpload} style={primaryBtn}>
          <Upload size={16} aria-hidden="true" /> Upload document
        </button>
      </div>
      <p style={{ color: 'var(--color-text-soft)', marginTop: 6, marginBottom: 20 }}>
        Documents securely submitted for your patients by providers and nurses. Newest first.
      </p>

      {showUpload && (
        <div style={formCard}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>Upload a document</div>
          <div style={formGrid}>
            <label style={lbl}>Patient
              {!newPatientMode ? (
                <SearchableSelect
                  value={form.patientId}
                  onChange={v => setForm(f => ({ ...f, patientId: v }))}
                  ariaLabel="Patient"
                  placeholder="Search patients…"
                  options={patients.map(p => ({ value: p.id, label: p.display_name + ((p as any).dob ? ` (DOB ${formatDob((p as any).dob)})` : '') }))}  /* BA-24: DOB, not MRN */
                  style={{ marginTop: 4 }}
                />
              ) : (
                <>
                  <SearchableSelect
                    value={placeholderSel}
                    onChange={v => { setPlaceholderSel(v); setSubmitMsg(null) }}
                    ariaLabel="Your placeholder patient"
                    placeholder="Search your placeholders…"
                    options={[
                      ...myPlaceholders.map(p => ({ value: p.id, label: p.display_name + (p.date_of_birth ? ` (DOB ${p.date_of_birth})` : '') })),
                      { value: '__new__', label: '+ Create a new placeholder' },
                    ]}
                    style={{ marginTop: 4 }}
                  />
                  {placeholderSel === '__new__' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                      <input value={newPatient.name} onChange={e => setNewPatient(n => ({ ...n, name: e.target.value }))} style={{ ...field, width: '100%' }} placeholder="New patient name" />
                      <input type="date" value={newPatient.dob} onChange={e => setNewPatient(n => ({ ...n, dob: e.target.value }))} style={{ ...field, width: '100%', minHeight: 44 }} aria-label="Date of birth" />
                    </div>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  const entering = !newPatientMode
                  setNewPatientMode(entering); setSubmitMsg(null); setPlaceholderSel('')
                  if (entering) api.getMySecureSharePlaceholders().then(r => setMyPlaceholders(r.items ?? [])).catch(e => setSubmitMsg(debugError('Add new (load your placeholders)', e)))
                }}
                style={{ alignSelf: 'flex-start', marginTop: 4, background: 'none', border: 'none', padding: 0, color: 'var(--color-primary, #2563eb)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
              >
                {newPatientMode ? '← Choose an existing patient' : 'Patient not listed? Add a placeholder'}
              </button>
            </label>
            <label style={lbl}>Document type
              <SearchableSelect
                value={form.documentType}
                onChange={v => setForm(f => ({ ...f, documentType: v }))}
                ariaLabel="Document type"
                options={DOC_TYPES.map(([k, v]) => ({ value: k, label: v }))}
                style={{ marginTop: 4 }}
              />
            </label>
            <label style={lbl}>File
              <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} style={field} />
            </label>
            <label style={lbl}>Title (optional)
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={field} />
            </label>
            <label style={{ ...lbl, gridColumn: '1 / -1' }}>Notes (optional)
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...field, minHeight: 60, resize: 'vertical' }} />
            </label>
          </div>
          {submitMsg && <div role="alert" style={{ color: 'var(--color-danger, #b00020)', marginTop: 10, fontSize: 13 }}>{submitMsg}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={submitUpload} disabled={submitting} style={primaryBtn}>{submitting ? 'Uploading…' : 'Upload'}</button>
            <button onClick={() => { setShowUpload(false); setSubmitMsg(null) }} disabled={submitting} style={secondaryBtn}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={alertStyle}>{error} <button onClick={() => load(q.trim().length >= 2 ? q.trim() : undefined)} style={linkBtn}>Retry</button></div>
      )}

      {!docs && !error && <div style={{ color: 'var(--color-text-soft)' }}>Loading…</div>}

      {docs && docs.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '0 0 10px' }}>
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Search by patient, DOB, document, or uploader…"
                 aria-label="Search documents"
                 style={{ flex: 1, minWidth: 220, maxWidth: 340, padding: '7px 10px', fontSize: 13, border: '1px solid var(--color-border, #ddd)', borderRadius: 8 }} />
          <SearchableSelect value={statusFilter} onChange={setStatusFilter} ariaLabel="Filter by status"
            placeholder="All statuses" style={{ minWidth: 150 }}
            options={[{ value: '', label: 'All statuses' }, ...api.SECURE_SHARE_STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') }))]} />
          <SearchableSelect value={typeFilter} onChange={setTypeFilter} ariaLabel="Filter by document type"
            placeholder="All types" style={{ minWidth: 170 }}
            options={[{ value: '', label: 'All types' }, ...DOC_TYPES.map(([k, v]) => ({ value: k, label: v }))]} />
          <span style={{ fontSize: 12.5, color: 'var(--color-text-mute, #888)' }}>{shown.length} of {docs.length}</span>
        </div>
      )}

      {docs && docs.length === 0 && (
        <div style={emptyStyle}>
          <FileText size={28} aria-hidden="true" style={{ opacity: 0.5 }} />
          <div style={{ marginTop: 10, fontWeight: 600 }}>No documents yet</div>
          <div style={{ color: 'var(--color-text-soft)', marginTop: 4 }}>
            When a provider or nurse sends a document in via Secure Share, it will appear here.
          </div>
        </div>
      )}

      {docs && docs.length === 500 && (
        <div role="status" style={{ border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f', fontSize: 12.5, borderRadius: 8, padding: '7px 10px', marginBottom: 10 }}>
          Showing the newest 500 documents — search to reach older ones.
        </div>
      )}

      {docs && docs.length > 0 && (
        <div style={{ overflow: 'visible' }}>{/* BA-25b: overflowX:auto clipped the open status dropdown */}
          <ColumnFilterChips filters={colFilters} labels={{ patient_name: 'Patient', patient_dob: 'DOB', title: 'Document', lifecycle_status: 'Status', received_at: 'Received', uploaded_by: 'Uploaded by' }} onClear={k => setColFilters(f => ({ ...f, [k]: new Set() }))} onClearAll={() => setColFilters({})} />
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="documents" />
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border, #e2e2e2)' }}>
                {sortTh('patient_name', 'Patient')}
                {sortTh('patient_dob', 'DOB')}
                {sortTh('title', 'Document')}
                {sortTh('lifecycle_status', 'Status')}
                {sortTh('received_at', 'Received')}
                {sortTh('uploaded_by', 'Uploaded by')}
                <th style={th} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map(d => (
                <Fragment key={d.id}>
                {/* `undefined`, NOT 'transparent': an inline background — even a transparent
                    one — beats the global zebra rule (index.css), which would have left this
                    the one un-striped table in the app. The scroll-to highlight still fades. */}
                <tr id={`ssdoc-${d.id}`} style={{ borderBottom: '1px solid var(--color-border, #eee)', transition: 'background-color 0.6s ease', backgroundColor: highlightId === d.id ? 'var(--color-accent-soft, #fff7d6)' : undefined }}>
                  <td style={td}>{d.patient_name ?? '—'}</td>
                  <td style={td}>{d.patient_dob ? formatDob(d.patient_dob) : '—'}</td>
                  <td style={td}>
                    {d.title || d.file_name || d.document_type || 'Document'}
                    {d.submitted_by_external && (
                      <span style={pill}>external</span>
                    )}
                  </td>
                  <td style={td}>
                    {isProviderViewer ? (
                      <span style={{ fontSize: 13, color: 'var(--color-text-mute)', textTransform: 'capitalize' }}>
                        {(d.lifecycle_status || 'received').replace(/_/g, ' ')}
                      </span>
                    ) : (
                      <SearchableSelect
                        value={api.SECURE_SHARE_STATUSES.includes(d.lifecycle_status as any) ? (d.lifecycle_status as string) : 'received'}
                        onChange={v => changeStatus(d, v)}
                        ariaLabel="Document status"
                        options={api.SECURE_SHARE_STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') }))}
                        style={{ minWidth: 150 }}
                      />
                    )}
                  </td>
                  <td style={td}>{d.received_at ? formatUsDateTime(d.received_at) : '—'}</td>
                  <td style={td}>{d.uploaded_by ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button onClick={() => openDoc(d)} disabled={busyId === d.id} style={openBtn}>
                        <ExternalLink size={14} aria-hidden="true" />
                        {busyId === d.id ? 'Opening…' : 'Open'}
                      </button>
                      <button onClick={() => setOpenComments(prev => prev === d.id ? null : d.id)} style={{ ...openBtn, display: COMMENTS_ENABLED ? undefined : 'none' }} aria-expanded={openComments === d.id} aria-label="Comments">
                        <MessageSquare size={14} aria-hidden="true" />
                        Comments
                      </button>
                      {d.can_delete && (
                        <button onClick={() => deleteDoc(d)} disabled={busyId === d.id} style={deleteBtn} aria-label="Delete document">
                          <Trash2 size={14} aria-hidden="true" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {COMMENTS_ENABLED && openComments === d.id && (
                  <tr>
                    <td colSpan={5} style={{ padding: '0 12px 14px', background: 'var(--color-bg-subtle,#fafafa)' }}>
                      <DocumentComments docId={d.id} isProvider={isProviderViewer} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="documents" />
        </div>
      )}
      <TypedDeleteConfirm
        open={!!pendingDelete}
        title="Delete document permanently?"
        message={`You are deleting "${pendingDelete?.title || pendingDelete?.file_name || 'this document'}"${pendingDelete?.patient_name ? ` for ${pendingDelete.patient_name}` : ''} from Secure Share.`}
        busy={!!pendingDelete && busyId === pendingDelete.id}
        onConfirm={reallyDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

const wrap: CSSProperties = { padding: 24, maxWidth: '100%', margin: '0 auto' }  // BA-25b: full-width layout
const th: CSSProperties = { padding: '8px 10px', fontSize: 12, color: 'var(--color-text-soft)', fontWeight: 600 }
const td: CSSProperties = { padding: '10px', fontSize: 14, verticalAlign: 'top' }
const openBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
  border: '1px solid var(--color-border, #ccc)', background: 'var(--color-surface, #fff)', cursor: 'pointer', fontSize: 13,
}
const deleteBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
  border: '1px solid var(--color-danger, #b00020)', background: 'var(--color-surface, #fff)', color: 'var(--color-danger, #b00020)', cursor: 'pointer', fontSize: 13,
}
const linkBtn: CSSProperties = { marginLeft: 8, background: 'none', border: 'none', color: 'var(--color-primary, #2563eb)', cursor: 'pointer', textDecoration: 'underline' }
const alertStyle: CSSProperties = { background: 'var(--color-danger-soft, #fde8e8)', color: 'var(--color-danger, #b00020)', padding: '10px 12px', borderRadius: 8, marginBottom: 14 }
const emptyStyle: CSSProperties = { textAlign: 'center', padding: '48px 0', border: '1px dashed var(--color-border, #e2e2e2)', borderRadius: 10 }
const pill: CSSProperties = { marginLeft: 8, fontSize: 11, padding: '1px 7px', borderRadius: 999, background: 'var(--color-surface-alt, #f1f5f9)', color: 'var(--color-text-soft)' }
const primaryBtn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn: CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border, #ccc)', background: 'var(--color-surface, #fff)', color: 'var(--color-text)', fontSize: 14, cursor: 'pointer' }
const formCard: CSSProperties = { border: '1px solid var(--color-border, #e2e2e2)', borderRadius: 10, padding: 18, marginBottom: 20, background: 'var(--color-surface, #fff)' }
const formGrid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }
const lbl: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--color-text-soft)' }
const field: CSSProperties = { boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border, #ccc)', background: 'var(--color-surface, #fff)', fontSize: 16, fontWeight: 400, color: 'var(--color-text)' }
